import { createEffect, createSignal, on, Show, onCleanup, onMount, /* useContext, */ Match, Switch, useContext } from 'solid-js';
import { StyledVideoContainer } from './video-iframe-container.style';
import { getYoutubeVideoId, getVimeoUrl } from '../../components/video-embed/helpers';
import { VideoImageBeforeCookiesAccepted } from '../../components/video-embed/video-image-before-cookie';
import { CookieModal } from '../cookie-modal/cookie-modal';
import { TabHandler } from '../../components/menu/menu-desktop/tab-handling';
import { AppContext } from '../../app-context-provider/app-context-provider';

type VideoIframeContainerProps = {
    videoLink?: string;
};


export const VideoIframeContainer = (props: VideoIframeContainerProps) => {
    const { localize } = useContext(AppContext);

    const [ isLoading, setIsLoading ] = createSignal(false);
    const [showModal, setShowModal] = createSignal(false);
    const [autoplayVideo, setAutoplayVideo] = createSignal(false);
    const [videoCookiesAccepted, setVideoCookiesAccepted] = createSignal(false);

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    createEffect(() => {
        setIsLoading(true);
    });

    createEffect(
        on(
            () => props.videoLink,
            () => {                
                setIsLoading(false);
                setTimeout(() => {
                    setIsLoading(true);
                }, 10);
            }
        )
    );

    onMount(() => {
        checkConsent();

        // Listen for changes in consent
        window.addEventListener('CookiebotOnAccept', () => {
            checkConsent();
        });

        onCleanup(() => {
            window.removeEventListener('CookiebotOnAccept', checkConsent);
        });
    });

    const checkConsent = () => {
        if (window?.Cookiebot?.consent?.marketing) {
            setVideoCookiesAccepted(true);
        }
    };

    const handleThumbnailClick = () => {
        setAutoplayVideo(true);
        setShowModal(true);
    };

    return (
        <Show when={props.videoLink}>
            <StyledVideoContainer
                isLoading={isLoading()}
                onClick={() => !videoCookiesAccepted() ? handleThumbnailClick() : null}
                videoCookiesAccepted={videoCookiesAccepted()}
                aria-label={localize('open-video', 'Open video')}
            >
                <Switch>
                    <Match when={videoCookiesAccepted()}>
                        <Show when={props.videoLink?.includes('youtu')}>
                            <iframe
                                src={`https://www.youtube-nocookie.com/embed/${getYoutubeVideoId(props.videoLink!)}?autoplay=${autoplayVideo() ? 1 : 0}&mute=${autoplayVideo() ? 1 : 0}`}
                                title="YouTube video player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowfullscreen
                                referrerpolicy="strict-origin-when-cross-origin"
                            ></iframe>
                        </Show>
                        <Show when={props.videoLink?.includes('vimeo')}>
                            <iframe
                                src={getVimeoUrl(props.videoLink!)}
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowfullscreen
                                referrerpolicy="strict-origin-when-cross-origin"
                            ></iframe>
                        </Show>
                    </Match>
                    <Match when={!videoCookiesAccepted()}>
                        <VideoImageBeforeCookiesAccepted 
                            videoUrl={props.videoLink!}
                            withoutBorderRadius={true}
                        />
                    </Match>
                </Switch>

                <Show when={showModal()}>
                    <TabHandler refs={{
                        firstTabbableElement,
                        lastTabbableElement,
                    }}>
                        <CookieModal
                            close={() => setShowModal(false)}
                            setFirstTabbableElement={(el: HTMLElement) => setFirstTabbableElement(el)}
                            setLastTabbableElement={(el: HTMLElement) => setLastTabbableElement(el)}
                        />
                    </TabHandler>
                </Show>

            </StyledVideoContainer>
        </Show>
    );
};

